import { useCallback } from "react";

/* ── Reassurance pills ─────────────────────────────── */
const pills = [
  { icon: "ri-truck-line", text: "Déplacement gratuit" },
  { icon: "ri-hammer-line", text: "Pièce comprise" },
  { icon: "ri-file-list-3-line", text: "Devis ferme" },
  { icon: "ri-shield-check-line", text: "Toutes marques" },
];

/* ── Volet roulant cards ───────────────────────────── */
const volets = [
  {
    prestation: "Réparation & dépannage",
    prix: "69",
    icon: "ri-tools-line",
    type: "reparation",
    featured: true,
  },
  {
    prestation: "Remplacement du moteur",
    prix: "279",
    icon: "ri-flashlight-line",
    type: "moteur",
    featured: false,
  },
  {
    prestation: "Motorisation",
    prix: "349",
    icon: "ri-settings-4-line",
    type: "motorisation",
    featured: false,
  },
];

/* ── Stores extérieurs cards ───────────────────────── */
const stores = [
  {
    prestation: "Réparation & dépannage",
    prix: "85",
    icon: "ri-store-2-line",
    type: "store",
    featured: false,
  },
  {
    prestation: "Remplacement du moteur",
    prix: "279",
    icon: "ri-flashlight-fill",
    type: "store-moteur",
    featured: false,
  },
];

/* ── Shared card component ─────────────────────────── */
interface TarifItem {
  prestation: string;
  prix: string;
  icon: string;
  type: string;
  featured?: boolean;
}

function TarifCard({ item, onRDV }: { item: TarifItem; onRDV: (type: string) => void }) {
  const isFeatured = item.featured;
  return (
    <div
      className={`relative bg-white rounded-xl p-6 flex flex-col items-center text-center transition-transform duration-300 hover:-translate-y-1 ${
        isFeatured
          ? "border-2 border-brand-cyan"
          : "border border-brand-border"
      }`}
    >
      {/* Badge featured */}
      {isFeatured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-cyan text-white text-xs font-semibold px-4 py-1 rounded-full max-w-[90%] text-center overflow-hidden">
          Le plus demandé
        </div>
      )}

      {/* Icon circle */}
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-cyan mb-5">
        <i className={`${item.icon} text-white text-2xl`}></i>
      </div>

      {/* Title */}
      <h3 className="text-brand-blue text-base font-bold mb-1">
        {item.prestation}
      </h3>

      {/* Subline */}
      <p className="text-[#6E869C] text-sm mb-1">
        Pièces, main d&apos;œuvre et déplacement compris
      </p>

      {/* "à partir de" */}
      <p className="text-[#6E869C] text-sm mb-1">à partir de</p>

      {/* Price */}
      <p className="text-brand-blue text-4xl font-extrabold mb-4">
        {item.prix}
        <span className="text-brand-cyan text-2xl font-bold"> €</span>
        <span className="text-[#6E869C] text-sm font-medium ml-1">HTVA</span>
      </p>

      {/* CTA button */}
      <button
        onClick={() => onRDV(item.type)}
        className={`mt-auto inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-colors cursor-pointer w-full text-center overflow-hidden justify-center ${
          isFeatured
            ? "bg-brand-cyan text-white hover:bg-brand-cyan/90"
            : "border-2 border-brand-cyan text-brand-cyan hover:bg-brand-cyan hover:text-white"
        }`}
      >
        Prendre RDV
      </button>
    </div>
  );
}

/* ── Main component ────────────────────────────────── */
export default function CartesTarifs() {
  const handlePrendreRDV = useCallback((type: string) => {
    window.location.hash = `devis?type=${type}`;
    const el = document.getElementById("devis");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <section className="bg-brand-light py-14 md:py-20">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* ── GROUPE Volet roulant ── */}
        <div className="mb-10 md:mb-14">
          <div className="flex items-center gap-2 mb-6 md:mb-8">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-cyan/10">
              <i className="ri-home-4-line text-brand-cyan text-base"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-brand-blue">
              Volet roulant
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {volets.map((t) => (
              <TarifCard key={t.type} item={t} onRDV={handlePrendreRDV} />
            ))}
          </div>
        </div>

        {/* ── GROUPE Stores extérieurs ── */}
        <div>
          <div className="flex items-center gap-2 mb-6 md:mb-8">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-cyan/10">
              <i className="ri-store-2-line text-brand-cyan text-base"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-brand-blue">
              Stores extérieurs
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {stores.map((t) => (
              <TarifCard key={t.type} item={t} onRDV={handlePrendreRDV} />
            ))}
          </div>
        </div>

        {/* ── Disclaimer ── */}
        <p className="text-[#6E869C] text-sm text-center mt-10 md:mt-14 max-w-2xl mx-auto leading-relaxed">
          Tarifs de départ, pièce comprise. Prix définitif confirmé par devis
          gratuit après diagnostic.
        </p>
      </div>
    </section>
  );
}